import React, { useState, useRef } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button, Alert } from '@/components/ui';
import { VenuesService } from '@/services/VenuesService';
import { useNotificationStore } from '@/stores/notificationStore';
import { Download } from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

export function VenueImportModal({ isOpen, onClose, onImportComplete }: Props) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { push: addNotification } = useNotificationStore();
  const venuesService = new VenuesService();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        addNotification({
          type: 'error',
          title: 'Invalid file',
          message: 'Please select a CSV file'
        });
        return;
      }
      setSelectedFile(file);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setIsImporting(true);
    try {
      const importResult = await venuesService.importCSV(selectedFile);
      setResult(importResult);
      
      addNotification({
        type: 'success',
        title: 'Import Complete',
        message: `Processed ${importResult.processed} rows: ${importResult.inserted} inserted, ${importResult.updated} updated`
      });

      if (importResult.success) {
        setTimeout(() => {
          onImportComplete();
          handleClose();
        }, 2000);
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Import Failed',
        message: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setResult(null);
    onClose();
  };

  const handleDownloadSample = () => {
    // Sample CSV data matching the import template
    const sampleCSV = `region,country,state,city,property name,website,address,number of rooms,meeting spaces,maximum capacity,star rating,travel time in minutes from the airport,nearest airport,notes
Europe,Portugal,,Lisbon,Hotel Tivoli Avenida Liberdade,https://www.tivolihotels.com,Avenida da Liberdade 185,306,12,800,5,20,LIS,Luxury hotel in city center
Europe,Spain,Catalonia,Barcelona,W Barcelona,https://www.marriott.com,Placa de la Rosa dels Vents 1,473,8,600,5,25,BCN,Beachfront iconic hotel
North America,USA,California,San Francisco,Palace Hotel,https://www.sfpalace.com,2 New Montgomery St,556,15,1200,5,30,SFO,Historic landmark hotel
North America,USA,New York,New York City,The Plaza Hotel,https://www.theplazany.com,768 5th Avenue,282,10,500,5,40,JFK,Iconic luxury hotel Central Park
Europe,Czech Republic,,Prague,Hotel Paris Prague,https://www.hotelparis.cz,U Obecniho domu 1,86,4,120,5,35,PRG,Art Nouveau architecture
Asia,Thailand,,Bangkok,Mandarin Oriental Bangkok,https://www.mandarinoriental.com,48 Oriental Avenue,393,11,900,5,45,BKK,Riverside luxury hotel
North America,Mexico,Quintana Roo,Cancun,Moon Palace Cancun,https://www.palaceresorts.com,Carretera Cancun-Chetumal Km 340,2457,20,3000,5,25,CUN,All-inclusive beach resort
Europe,United Kingdom,England,London,The Savoy,https://www.thesavoylondon.com,Strand WC2R 0EZ,267,12,600,5,60,LHR,Historic Thames-side hotel
Asia,Japan,,Tokyo,Park Hyatt Tokyo,https://www.hyatt.com,3-7-1-2 Nishi Shinjuku,177,8,300,5,75,NRT,Luxury skyscraper hotel
North America,Canada,Ontario,Toronto,Fairmont Royal York,https://www.fairmont.com,100 Front St W,1365,25,2000,4,35,YYZ,Historic downtown hotel
Europe,Italy,Lazio,Rome,Rome Cavalieri Waldorf Astoria,https://www.hilton.com,Via Alberto Cadlolo 101,370,14,800,5,40,FCO,Hilltop luxury hotel
South America,Brazil,Rio de Janeiro,Rio de Janeiro,Belmond Copacabana Palace,https://www.belmond.com,Avenida Atlantica 1702,239,10,500,5,30,GIG,Iconic beachfront hotel
Oceania,Australia,New South Wales,Sydney,Park Hyatt Sydney,https://www.hyatt.com,7 Hickson Rd,155,6,250,5,25,SYD,Harbor views luxury hotel
Middle East,UAE,Dubai,Dubai,Burj Al Arab Jumeirah,https://www.jumeirah.com,Jumeirah St,201,7,400,5,20,DXB,Iconic sail-shaped hotel
Africa,Morocco,,Marrakech,La Mamounia,https://www.mamounia.com,Avenue Bab Jdid,209,9,450,5,15,RAK,Palace hotel with gardens`;

    // Create blob and download
    const blob = new Blob([sampleCSV], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'venues-import-sample.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    addNotification({
      type: 'success',
      title: 'Sample Downloaded',
      message: 'Sample CSV file has been downloaded'
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Import Venues CSV">
      <div className="space-y-4">
        <Alert>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="font-medium">CSV Format</div>
              <div className="text-sm mt-1">
                Expected columns: region, country, state, city, property name, website, 
                address, number of rooms, meeting spaces, maximum capacity, star rating, 
                travel time in minutes from the airport, nearest airport, notes
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownloadSample}
              leftIcon={<Download className="w-4 h-4" />}
              className="ml-4 flex-shrink-0"
            >
              Download Sample
            </Button>
          </div>
        </Alert>

        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            className="hidden"
          />
          <div className="text-center">
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
            >
              Select CSV File
            </Button>
            {selectedFile && (
              <p className="mt-2 text-sm text-gray-600">{selectedFile.name}</p>
            )}
          </div>
        </div>

        {result && (
          <div className="space-y-2">
            <div className="grid grid-cols-4 gap-2 text-sm">
              <div>
                <div className="font-medium">Processed</div>
                <div>{result.processed}</div>
              </div>
              <div>
                <div className="font-medium">Inserted</div>
                <div className="text-green-600">{result.inserted}</div>
              </div>
              <div>
                <div className="font-medium">Updated</div>
                <div className="text-blue-600">{result.updated}</div>
              </div>
              <div>
                <div className="font-medium">Errors</div>
                <div className="text-red-600">{result.errors.length}</div>
              </div>
            </div>

            {result.errors.length > 0 && (
              <div className="mt-4">
                <div className="font-medium text-sm">Errors:</div>
                <div className="mt-1 max-h-32 overflow-y-auto text-xs bg-red-50 p-2 rounded">
                  {result.errors.slice(0, 10).map((error: any, i: number) => (
                    <div key={i}>Row {error.row}: {error.message}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={!selectedFile || isImporting}
          >
            {isImporting ? 'Importing...' : 'Import'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
